import { useEffect, useRef, useState } from 'react';
import { addDays, format, parseISO } from 'date-fns';
import { getOutingAvailability } from '../../brain/api.js';
import { resolveAvailabilityCalendarStartDate } from '../Blogs/AvailabilityCalendar.jsx';

/** Prefetch window covers desktop (7) and mobile calendar (up to 14). */
export const PREFETCH_DAYS = 14;

function buildWindowStarts(initialStartDate) {
  const start = parseISO(initialStartDate);
  const desktopNext = format(addDays(start, 7), 'yyyy-MM-dd');
  const mobileNext = format(addDays(start, 14 - start.getDay()), 'yyyy-MM-dd');
  return [...new Set([initialStartDate, desktopNext, mobileNext])];
}

async function fetchWindow({
  slug,
  windowStart,
  groupSize,
  campsiteIds,
}) {
  const endDate = format(
    addDays(parseISO(windowStart), PREFETCH_DAYS),
    'yyyy-MM-dd'
  );
  const response = await getOutingAvailability({
    slug,
    startDate: windowStart,
    endDate,
    groupSize: Math.max(1, Number(groupSize) || 1),
    campsiteIds: campsiteIds?.length ? campsiteIds : undefined,
  });
  if (response?.success?.places?.length > 0) {
    return response.success;
  }
  return null;
}

/**
 * Prefetch live availability for the TrackWidget. Returns payload only once the
 * first window is ready. Also fetches the likely next desktop/mobile windows
 * into `windows` so the calendar can open and page forward without waiting.
 */
export default function usePrefetchLiveAvailability({
  enabled,
  slug,
  startDate,
  groupSize,
  campsiteIds,
}) {
  const [payload, setPayload] = useState(null);
  const campsiteKey = Array.isArray(campsiteIds) ? campsiteIds.join(',') : '';
  const startKey =
    startDate instanceof Date && !Number.isNaN(startDate.getTime())
      ? format(startDate, 'yyyy-MM-dd')
      : '';
  const windowsRef = useRef({});

  useEffect(() => {
    if (!enabled || !slug) {
      windowsRef.current = {};
      setPayload(null);
      return undefined;
    }

    let cancelled = false;
    const initialStartDate = resolveAvailabilityCalendarStartDate(
      slug,
      startKey || undefined
    );
    const windowStarts = buildWindowStarts(initialStartDate);

    windowsRef.current = {};
    setPayload(null);

    const partySize = Math.max(1, Number(groupSize) || 1);
    const ids = campsiteIds?.length ? campsiteIds : undefined;

    (async () => {
      try {
        const first = await fetchWindow({
          slug,
          windowStart: initialStartDate,
          groupSize: partySize,
          campsiteIds: ids,
        });
        if (cancelled) return;
        if (!first) {
          setPayload(null);
          return;
        }
        windowsRef.current[initialStartDate] = first;
        setPayload({
          data: first,
          initialStartDate,
          windows: { ...windowsRef.current },
        });

        // Background: remaining next-window candidates (desktop + mobile).
        const rest = windowStarts.filter((s) => s !== initialStartDate);
        await Promise.all(
          rest.map(async (windowStart) => {
            try {
              const data = await fetchWindow({
                slug,
                windowStart,
                groupSize: partySize,
                campsiteIds: ids,
              });
              if (cancelled || !data) return;
              windowsRef.current[windowStart] = data;
              setPayload((prev) =>
                prev
                  ? { ...prev, windows: { ...windowsRef.current } }
                  : prev
              );
            } catch {
              // Ignore background failures; calendar can fetch on navigate.
            }
          })
        );
      } catch {
        if (!cancelled) setPayload(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, slug, startKey, groupSize, campsiteKey]);

  return payload;
}
